import { cn } from '@today/ui-primitives'
import type { ReactElement, ReactNode } from 'react'

/**
 * Built-in strings of {@link PermissionDeniedView}, overridable per locale (tech §59). Each is a
 * default used only when the consumer omits the matching prop; neither names a business entity.
 */
export interface PermissionDeniedViewLabels {
	/** Default heading when `title` is omitted. */
	title: string
	/** Default explanation when `reason` is omitted. */
	reason: string
}

const DEFAULT_LABELS: PermissionDeniedViewLabels = {
	title: 'Action not available',
	reason: 'You can view this, but acting here needs additional permissions.',
}

/**
 * Props for the {@link PermissionDeniedView} panel.
 *
 * @property title - Heading naming what is restricted; falls back to `labels.title`.
 * @property reason - The "why" in plain language; falls back to `labels.reason`. Supplied by the
 *   consumer so the component stays domain-agnostic (it never maps an atom to copy).
 * @property labels - Localised built-in defaults; each omitted key falls back to English.
 * @property icon - Optional illustrative node replacing the default lock, hidden from assistive tech.
 * @property className - Extra Tailwind classes merged onto the container.
 *
 * @example
 * ```tsx
 * <PermissionDeniedView title="Creating requests is restricted" reason="Super-admin only" />
 * ```
 */
export interface PermissionDeniedViewProps {
	title?: string
	reason?: ReactNode
	labels?: Partial<PermissionDeniedViewLabels>
	icon?: ReactNode
	className?: string
}

/**
 * Padlock marking the region as access-restricted. Decorative — the alert role and the reason
 * carry the meaning for assistive tech, so the icon is `aria-hidden` and tinted with `ds-text-muted`.
 */
function LockIcon(): ReactElement {
	return (
		<span data-testid="permission-denied-icon" aria-hidden="true" className="text-ds-text-muted">
			<svg
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				aria-hidden="true"
			>
				<rect x="4" y="11" width="16" height="9" rx="2" />
				<path d="M8 11V7a4 4 0 0 1 8 0v4" strokeLinecap="round" />
			</svg>
		</span>
	)
}

/**
 * Inline, section-level permission-denied panel (TO-642, ux-interaction-standards §3; ADR-0063
 * read-mostly). Shown where an act-gated section's content would be, for a viewer who may READ the
 * surrounding screen but not ACT here — instead of a mid-page 403 or a redirect.
 *
 * @remarks
 * - **No action.** A denied viewer has nothing to act on, so the panel renders no button or link
 *   (mirrors the `no-permission` empty-state variant). It is NOT the same as that variant: this is a
 *   populated section whose actions are gated, not an empty collection.
 * - **Copy is supplied.** `title` and `reason` come from the consumer (the screen decides the "why");
 *   the built-in defaults name no business entity.
 * - **Accessibility:** the container is `role="alert"` so a screen reader announces it on appearance;
 *   the icon is decorative (`aria-hidden`).
 * - **Styling:** every colour, radius, spacing and type class is a `ds-*` token utility.
 *
 * @see packages/ui-domain/src/authorization/permission-denied-view.stories.tsx for visual examples
 */
export function PermissionDeniedView({
	title,
	reason,
	labels,
	icon,
	className,
}: PermissionDeniedViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...labels }
	const heading = title ?? text.title
	const body = reason ?? text.reason
	return (
		<div
			role="alert"
			data-denied-scope="section"
			className={cn(
				'flex flex-col items-center gap-3 rounded-ds-lg border border-ds-border p-8 text-center',
				className,
			)}
		>
			{icon ? (
				<span data-testid="permission-denied-icon" aria-hidden="true" className="text-ds-text-muted">
					{icon}
				</span>
			) : (
				<LockIcon />
			)}
			<h3 className="text-ds-heading-3 text-ds-text">{heading}</h3>
			<p className="text-ds-body text-ds-text-muted">{body}</p>
		</div>
	)
}
